import React, { SyntheticEvent, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { get, cloneDeep } from 'lodash';
import { Drawer, ListItem, ListItemText, List, Divider } from '@material-ui/core';
// import SearchIcon from '@material-ui/icons/Search';
import Qpopper from '../QPopper';
import DropDownMenu from './DropDownNav';
import {
  featureFlagNavKeyMapping,
  avoidSortNavigationUniqueId,
  SidebarNav,
  sideBarNavs,
  HelpCenterContentAI,
  HomeNavForContentAI,
  selectionToggleForEnterpriseUser,
  aiToolsNavConfig,
  contentAiNavConfig,
  sidebarDefaultAiSeoNavs,
  sidebarConfigAiToolsSpecificUsers,
} from '../../constants/navigationMeta';
import { isProductionEnv, processSubscriptionData, USER_CLASSIFICATION } from '../../utility/common';
import { Backdrop } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useIsTrial } from '../../utility/services/customHooks';
import { signOut } from '../../utility/services/authActions';
import { useIsSubscriberUser, useSchema, useTestDriveDomain } from '../../utility/services/customHooks';
import {
  useIsContentAIUser,
  useIsInternalUser,
  useIsTestDriveUser,
  useSession,
} from '../../utility/services/customHooks/auth';
import { SUBSCRIPTION_STATUS } from '../../constants/common';
import CreditsComponent from './CreditsComponent';
import PlanDetails from './PlanDetails';

type AnchorData = {
  ele?: Element | null;
  to?: string;
};

export default function Sidebar(props) {
  const [anchorData, setAnchorData] = React.useState<AnchorData>({});
  const schemaName = useSchema();
  const { session } = useSession();
  const customerInfo = useSelector((state) => state.root.customerInfo || []);
  const showSubscriberBanner = useSelector((state) => state.root.showSubscriberBanner);
  const customerData = customerInfo.find((o) => o.cust_code === schemaName);
  const router = useRouter();
  const { asPath } = router;
  const pathname = asPath;
  let sideBarNavOptions = cloneDeep([...sidebarDefaultAiSeoNavs]);
  let defaultSideBarNavOptions = cloneDeep([...sidebarDefaultAiSeoNavs]);
  let defaultSideBarForAiSeoUsers = cloneDeep([...sideBarNavs]);
  let aiToolsNav = cloneDeep([...aiToolsNavConfig]);
  let contentAiNav = cloneDeep([...contentAiNavConfig]);
  let sidebarConfigAiToolsSpecificUser = cloneDeep(sidebarConfigAiToolsSpecificUsers);

  const testDriveDomain = useTestDriveDomain();
  const fullYear = new Date()?.getFullYear();
  const [subscriberMeta, setSubscriberMeta] = React.useState<any>({});
  const SubscriptionStatus = get(session, 'user.subscription_status');
  const ToolsSubscription = get(session, 'user.qwa_tools_subscription_status');

  // default to "least" privileged options for information about the user
  // TODO we should "add" menu options rather than "remove" menu options
  // const isSubscriberUser = useIsSubscriberUser() ?? true;
  // const isInternalUser = useIsInternalUser() ?? false;
  // const isTestDriveUser = useIsTestDriveUser() ?? true;
  // const isTrial = useIsTrial() ?? true;

  // Avoid defaults due to long load times for now
  // TODO when the session is moved into redux or we migrate away from our solution
  // we can delete this code and uncomment the above
  const isSubscriberUser = useIsSubscriberUser();
  const isInternalUser = useIsInternalUser();
  const isTestDriveUser = useIsTestDriveUser();
  const IsContentAIUser = useIsContentAIUser();
  const isTrial = useIsTrial();

  const isCancelled =
    (SubscriptionStatus &&
      (SubscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED ||
        SubscriptionStatus === SUBSCRIPTION_STATUS.SUBSCRIPTION_PAUSED)) ||
    subscriberMeta.isSubscriptionCancelled;

  const isActiveToolsSubscription =
    ToolsSubscription &&
    [
      SUBSCRIPTION_STATUS.SUBSCRIPTION_ACTIVATED,
      SUBSCRIPTION_STATUS.SUBSCRIPTION_RESUMED,
      SUBSCRIPTION_STATUS.SUBSCRIPTION_REACTIVATED,
      SUBSCRIPTION_STATUS.SUBSCRIPTION_CREATED,
    ].includes(ToolsSubscription);
  // default to desktop when doing server side rendering/build time
  const isMobile = props.isMobile ?? false;
  const toggleDrawer = props.toggleDrawer;

  const { open, userType, defaultUser } = props;
  const role = get(customerData, 'user_role');
  const sideBarConfig = get(customerData, 'sidebar_link_config');
  const isDemoUser = customerData?.additional_user_info?.isDemoUser;

  const isMenuLinkVisible = useCallback(
    (key) => {
      if (defaultUser === 'CONTENT_AI' && isCancelled && !isActiveToolsSubscription) {
        if (key === 'user-type-selection') {
          return true;
        } else {
          return false;
        }
      }
      if (defaultUser === 'CONTENT_AI' && isActiveToolsSubscription && isCancelled) {
        if (userType === 'CONTENT_AI') {
          if (key === 'user-type-selection') {
            return true;
          } else {
            return false;
          }
        } else if (userType === 'QWA_TOOLS') {
          return true;
        }
      }

      if (
        defaultUser === 'QWA_TOOLS' &&
        !['user-type-selection'].includes(key) &&
        ['CONTENT_AI', 'AI_SEO_SUITE'].includes(userType)
      ) {
        return false;
      }
      if ((isDemoUser !== undefined && isDemoUser) || key === 'home') {
        return true;
      }
      if (userType !== undefined && userType == 'QWA_TOOLS') {
        return true;
      }
      return sideBarConfig?.[key]; // Default to false if the key is not present in the configuration
    },
    [isDemoUser, sideBarConfig, isCancelled, userType, defaultUser] // Add the necessary dependencies here
  );

  React.useEffect(() => {
    if (customerData && customerData?.SubscriberUserDetails) {
      const _subscriberMeta = processSubscriptionData(customerData);
      setSubscriberMeta(_subscriberMeta);
    }
  }, [customerData]);

  React.useEffect(() => {
    // to close drawer on path change
    if (isMobile) {
      toggleDrawer(false);
    }
    if (isInternalUser) {
      if (sideBarConfig) {
        if (sideBarConfig['il-autonomous'] === undefined) {
          sideBarConfig['il-autonomous'] = true;
        } else {
          sideBarConfig['il-autonomous'] = true;
        }
      }
    }
  }, [isMobile, pathname, toggleDrawer, isInternalUser, sideBarConfig]);

  if (!get(customerData, 'additional_user_info')) {
    sideBarNavOptions = sideBarNavOptions.filter((i) => !i.isDynamicNav);
    // keeping all static navs
  } else {
    const allowedDynamicNavs = Object.keys(featureFlagNavKeyMapping).map((i) => {
      if (get(customerData, `additional_user_info.${i}`)) {
        return featureFlagNavKeyMapping[i];
      } else {
        if (i === 'intentmanagement') {
          if (get(customerData, `additional_user_info.${i}`, true) !== false) {
            return featureFlagNavKeyMapping[i];
          }
        }
      }
      return 'N/A';
    });

    if (!isInternalUser) {
      sideBarNavOptions = sideBarNavOptions.filter(
        (i) => (i.isDynamicNav && allowedDynamicNavs.includes(i.key)) || !i.isDynamicNav || i.isInternal
      );
    }
    if (isCancelled && !isActiveToolsSubscription && !pathname.includes('/app/content-ai-home')) {
      router.push('/app/content-ai-home');
    }

    if (get(customerData, `additional_user_info.hideAdhocAnalysisNav`)) {
      // hiding adhoc analysis for MAGI
      sideBarNavOptions = sideBarNavOptions.filter((i) => i.key !== 'adhoc-analysis');
      if (!sideBarNavOptions.some((i) => i.key !== undefined && pathname.includes(i.key)) && !isDemoUser) {
        router.push('/404');
      }
    }
  }

  if (isSubscriberUser && (!customerData || testDriveDomain || role === undefined || role === 'EXTERNAL')) {
    sideBarNavOptions = sideBarNavOptions.filter((i) => {
      let flag = false;
      if (i?.isSubscriberUserShow) {
        if (i.key === 'trends' && i.subNavs) {
          i.subNavs = i.subNavs.filter((subnav) => !subnav.title.includes('Trends Watchlist'));
        }
        flag = true;
      }
      return flag;
    });
    if (isSubscriberUser && customerInfo.length === 1 && !testDriveDomain) {
      // if external user clears his localstorage he gets access to all subscribers contents. this is to avoid that.
      signOut();
    }
    if (customerData && !sideBarNavOptions.some((i) => i.key !== undefined && pathname.includes(i.key))) {
      router.push('/404');
    }
  }

  if (isTrial) {
    // extract content-ai sub section
    let contentAiStart = -1,
      contentAiEnd = -1;
    for (let sideBarIdx = 0; sideBarIdx < sideBarNavOptions.length; sideBarIdx++) {
      const currentNav = sideBarNavOptions[sideBarIdx];

      const startFound = contentAiStart !== -1;
      if (!startFound) {
        if (currentNav.isSection && currentNav.key === 'content-ai') {
          contentAiStart = sideBarIdx;
        }
      } else {
        const isEndOfSection = currentNav.isSection && currentNav.key !== 'content-ai';
        if (isEndOfSection) {
          contentAiEnd = sideBarIdx;

          // we found the start and end
          break;
        }
      }
    }

    /**
     * @type {import('../../constants/navigationMeta').SidebarNav[]}
     */
    let contentAiSubSection = [] as SidebarNav[];
    if (contentAiStart !== -1 && contentAiEnd !== -1) {
      contentAiSubSection = sideBarNavOptions.slice(contentAiStart, contentAiEnd);
    }

    // only keep content-ai options
    sideBarNavOptions = contentAiSubSection;
  }
  const setAnchor = (event: SyntheticEvent<Element, MouseEvent> | null, to?: any) => {
    setAnchorData({ ele: event?.currentTarget as Element | null, to });
  };

  const hoveredSubnavs = sideBarNavOptions.find((i) => anchorData?.to === i.key);
  if (sideBarConfig !== undefined) {
    const isAnlyticEnabled = avoidSortNavigationUniqueId?.some((key) => sideBarConfig[key]);
    //sort sidebar menus based on the activated tabs true in db config, taking sequentially
    if (!isAnlyticEnabled) {
      sideBarNavOptions.sort((a, b) => {
        // Find the uniqueKey values of a and b within the sideBarConfig object
        if (sideBarConfig !== undefined) {
          const uniqueKeyA = a.uniqueKey as keyof typeof sideBarConfig;
          const uniqueKeyB = b.uniqueKey as keyof typeof sideBarConfig;
          // Check if a and b exist in the sideBarConfig object and their values are true
          const hasTrueA = sideBarConfig[uniqueKeyA] === true;
          const hasTrueB = sideBarConfig[uniqueKeyB] === true;
          if (hasTrueA && !hasTrueB) {
            return -1; // a comes before b
          } else if (!hasTrueA && hasTrueB) {
            return 1; // b comes before a
          }
        }
        // Maintain the original order for items with the same condition or if the condition is not met
        return 0;
      });
      //Need in future if sort specific menus only
      // sideBarNavOptions.sort((a, b) => {
      //   const indexA = sideBarSectionOrder?.indexOf(a.uniqueKey ?? '');
      //   const indexB = sideBarSectionOrder?.indexOf(b.uniqueKey ?? '');
      //   if (indexA > -1 && indexB > -1) {
      //     return indexA - indexB; // Sort based on the position in the sideBarSectionOrder array
      //   } else if (indexA > -1) {
      //     return -1; // a comes before b
      //   } else if (indexB > -1) {
      //     return 1; // b comes before a
      //   }
      //   return 0; // maintain original order
      // });
    }
  }

  if (
    get(customerData, 'additional_user_info') &&
    !isDemoUser &&
    (IsContentAIUser || isMenuLinkVisible('is-content-enabled-only'))
  ) {
    sideBarNavOptions.splice(1, 0, HomeNavForContentAI);
    // sideBarNavOptions.splice(6, 0, HelpCenterContentAI);
  }

  //setting sidebar menu for ai_tools section
  if ([userType].includes('QWA_TOOLS')) {
    sideBarNavOptions = aiToolsNav;
  }
  //setting sidebar menu for content_ai section
  if ([userType].includes('CONTENT_AI')) {
    sideBarNavOptions = contentAiNav;
  }
  //setting sidebar menu for ai_seo_suite section
  if ([userType].includes('AI_SEO_SUITE') && defaultUser !== undefined) {
    sideBarNavOptions = defaultSideBarNavOptions;
  }
  //setting sidebar menu for ai_seo_suite user selecting content_ai
  if (defaultUser == undefined && ![userType].includes('QWA_TOOLS')) {
    sideBarNavOptions = defaultSideBarForAiSeoUsers;
  }

  //adding sidebar selector for ai_seo_suite user specific

  if (props?.isEnterpriseUser && !isMobile) {
    if (!sideBarNavOptions.includes(selectionToggleForEnterpriseUser) && defaultUser == undefined) {
      selectionToggleForEnterpriseUser.isAtBottom = true;
      sideBarNavOptions.splice(sideBarNavOptions.length, 0, selectionToggleForEnterpriseUser);
    } else if (!sideBarNavOptions.includes(selectionToggleForEnterpriseUser) && defaultUser !== undefined) {
      selectionToggleForEnterpriseUser.isAtBottom = false;
      sideBarNavOptions.splice(0, 0, selectionToggleForEnterpriseUser);
    }
  }
  //adding sidebar selector for ai_seo_suite user
  // if (props?.isEnterpriseUser && !isMobile) {
  //   if (!sideBarNavOptions.includes(selectionToggleForEnterpriseUser)) {
  //     selectionToggleForEnterpriseUser.isAtBottom = true;
  //     sideBarNavOptions.splice(sideBarNavOptions.length, 0, selectionToggleForEnterpriseUser);
  //   }
  // }
  //adding sidebar selector for mobile user
  if (isMobile) {
    if (!sideBarNavOptions.includes(selectionToggleForEnterpriseUser)) {
      selectionToggleForEnterpriseUser.isAtBottom = false;
      sideBarNavOptions.splice(0, 0, selectionToggleForEnterpriseUser);
    }
  }

  if (!isInternalUser) {
    sideBarNavOptions = sideBarNavOptions.filter((i) => {
      if (i.isInternal) {
        return false;
      } else if (i.key !== 'settings') {
        return true;
      } else if (i.subNavs) {
        const isSettingEnbaledCI = isMenuLinkVisible('is-content-enabled-only');
        if (!isSettingEnbaledCI) {
          return i.display === true;
        }
        return true;
      }
    });
    const intentFlag = get(customerData, 'additional_user_info', false);
    if (intentFlag) {
      const getintentFlag = get(intentFlag, 'intentmanagement', true);
      if (!getintentFlag && pathname.includes('/app/settings/')) {
        router.push('/404');
      }
    }
  }

  //[HOT-FIX]-Enable-multidomain-support-to-enterprise-customers
  if (!isInternalUser) {
    sideBarNavOptions = sideBarNavOptions?.map((nav) => {
      if (nav.subNavs && !isMenuLinkVisible('is-content-enabled-only')) {
        return {
          ...nav,
          subNavs: nav.subNavs.filter((subNav) => subNav.uniqueKey !== 'settings-setup'),
        };
      }
      return nav;
    });
  }
  if (![userType].includes('QWA_TOOLS')) {
    sideBarNavOptions.push(HelpCenterContentAI);
  }

  const getSideBarComponent = useCallback(() => {
    if (isTestDriveUser !== undefined) {
      return (
        sideBarNavOptions
          //.filter((nav) => isValid || isMenuLinkVisible(nav.uniqueKey))
          .map((nav) => (
            <>
              {!nav.isAtBottom ? (
                <div className={nav?.selectBox ? 'sidebar-select-box' : ''}>
                  <DropDownMenu
                    id={`layout__menu__side_bar__${nav.key}`}
                    key={nav.key}
                    title={nav.title}
                    name={nav.key}
                    setAnchor={setAnchor}
                    pathname={pathname}
                    icon={nav.icon}
                    subNavs={nav.subNavs}
                    isSection={nav.isSection}
                    sideBarConfig={
                      defaultUser !== USER_CLASSIFICATION.QWA_TOOLS ? sideBarConfig : sidebarConfigAiToolsSpecificUser
                    }
                    isDisabledMenu={isMenuLinkVisible(nav.uniqueKey)}
                    isDemoUser={isDemoUser}
                    isDrawerOpen={open}
                    to={nav.to}
                    toExternal={nav.toExternal}
                    IsPlaneNav={nav.IsPlaneNav}
                    keepDefaultExpanded={
                      userType !== USER_CLASSIFICATION.AI_SEO_SUITE &&
                      (IsContentAIUser || isMenuLinkVisible('is-content-enabled-only'))
                    }
                    isHelpCenter={nav.key === 'helpcenter'}
                    customerName={
                      customerData
                        ? customerData?.cust_name
                        : defaultUser === USER_CLASSIFICATION.QWA_TOOLS
                        ? USER_CLASSIFICATION.QWA_TOOLS
                        : null
                    }
                    selectBox={nav?.selectBox}
                    filterChange={props?.handleUserToggle}
                    options={props?.options}
                    userType={userType}
                    defaultUser={defaultUser}
                    isAtBottom={nav?.isAtBottom}
                    showNewBadge={nav?.showNewBadge}
                  />
                  <Divider />
                </div>
              ) : null}
            </>
          ))
      );
    }
  }, [isTestDriveUser, sideBarNavOptions, pathname, sideBarConfig, isDemoUser, open, IsContentAIUser, isInternalUser]);

  const getSideBarSelectAtBottomComponent = useCallback(() => {
    if (isTestDriveUser !== undefined) {
      return (
        sideBarNavOptions
          //.filter((nav) => isValid || isMenuLinkVisible(nav.uniqueKey))
          .map((nav) => (
            <>
              {nav?.selectBox && nav?.isAtBottom ? (
                <div className={nav?.selectBox ? 'sidebar-select-box' : ''}>
                  <DropDownMenu
                    id={`layout__menu__side_bar__${nav.key}`}
                    key={nav.key}
                    title={nav.title}
                    name={nav.key}
                    setAnchor={setAnchor}
                    pathname={pathname}
                    icon={nav.icon}
                    subNavs={nav.subNavs}
                    isSection={nav.isSection}
                    sideBarConfig={
                      defaultUser !== USER_CLASSIFICATION.QWA_TOOLS ? sideBarConfig : sidebarConfigAiToolsSpecificUser
                    }
                    isDisabledMenu={isMenuLinkVisible(nav.uniqueKey)}
                    isDemoUser={isDemoUser}
                    isDrawerOpen={open}
                    to={nav.to}
                    toExternal={nav.toExternal}
                    IsPlaneNav={nav.IsPlaneNav}
                    keepDefaultExpanded={IsContentAIUser || isMenuLinkVisible('is-content-enabled-only')}
                    isHelpCenter={nav.key === 'helpcenter'}
                    customerName={
                      customerData
                        ? customerData?.cust_name
                        : defaultUser === USER_CLASSIFICATION.QWA_TOOLS
                        ? USER_CLASSIFICATION.QWA_TOOLS
                        : null
                    }
                    selectBox={nav?.selectBox}
                    filterChange={props?.handleUserToggle}
                    options={props?.options}
                    userType={userType}
                    defaultUser={defaultUser}
                    isAtBottom={nav?.isAtBottom}
                    showNewBadge={nav?.showNewBadge}
                  />
                  <Divider />
                </div>
              ) : null}
            </>
          ))
      );
    }
  }, [isTestDriveUser, sideBarNavOptions, pathname, sideBarConfig, isDemoUser, open, IsContentAIUser, isInternalUser]);

  return (
    <>
      <Drawer
        variant="permanent"
        className={`drawer ${open ? 'open' : 'close'}  ${props.isMobile && open ? 'sidebar-mobile-open' : ''} ${
          showSubscriberBanner ? (props.isMobile ? '' : 'add-30-pd') : ''
        }`}
      >
        {props.isMobile ? <Backdrop open={open} onClick={() => props.toggleDrawer(false)} /> : null}
        <List className={`sidebarlistwrapper ${open ? 'sidebar-list-open' : 'sidebar-list-close'}`}>
          <div id="layout__div__side_bar_0" className="sidebar-options">
            <Divider />
            {getSideBarComponent()}
          </div>
          <div>
            <Divider className={` ${isProductionEnv ? 'd-none' : ''}`} />
            {userType == USER_CLASSIFICATION.QWA_TOOLS &&
            (props.defaultUser === USER_CLASSIFICATION.QWA_TOOLS ||
              (session?.user?.userType === USER_CLASSIFICATION.CONTENT_AI &&
                ![
                  SUBSCRIPTION_STATUS.SUBSCRIPTION_ACTIVATED,
                  SUBSCRIPTION_STATUS.SUBSCRIPTION_REACTIVATED,
                  SUBSCRIPTION_STATUS.SUBSCRIPTION_RESUMED,
                ].includes(session?.user?.subscription_status as any))) ? (
              <CreditsComponent />
            ) : null}
            {open && getSideBarSelectAtBottomComponent()}
            <div id="layout__div__side_bar_1" className="copyrightwrap">
              © Quattr Inc. {fullYear}
            </div>
          </div>
        </List>
      </Drawer>
      <Qpopper setAnchor={setAnchor} anchorEl={anchorData?.ele} placement="right-start" useDelayForClosing>
        <div id="layout__div__side_bar_2" className="popper-container" onMouseLeave={() => setAnchor(null)}>
          <List component="div" disablePadding>
            {hoveredSubnavs?.subNavs
              ? hoveredSubnavs.subNavs.map((item) => (
                  <Link key={item.to} href={item.to || ''}>
                    <ListItem button className={`list-item ${pathname === item.to ? 'active' : ''}`}>
                      <ListItemText primary={item.title} />
                    </ListItem>
                  </Link>
                ))
              : null}
          </List>
        </div>
      </Qpopper>
    </>
  );
}
